import {
  enableAlternateReviewerField,
  enableAlternateScheduledChangeForm,
} from '@gonfalon/dogfood-flags/dogfood-flags';

import { SaveOptions } from '#utils/saveButtonUtils';

export const getFormRenderingConfig = (params: {
  testRunEnabled: boolean;
  isCustomScheduleSelected: boolean;
  isUpdatingScheduledChange: boolean;
  reviewerCount: number;
  isApprovalRequired: boolean;
  isConfirmationRequired: boolean;
  hideApprovalOption: boolean;
  hideScheduleOption: boolean;
  usesLaunchDarklyApprovalService: boolean;
  isAcknowledgeConflictsRequired: boolean;
  bypassRequiredApprovals: boolean;
  showOptionalReviewers: boolean;
  canBypassRequiredApprovals: boolean;
  saveOption: SaveOptions;
}) => {
  const {
    testRunEnabled,
    isCustomScheduleSelected,
    isUpdatingScheduledChange,
    reviewerCount,
    isApprovalRequired,
    isConfirmationRequired,
    hideApprovalOption,
    hideScheduleOption,
    usesLaunchDarklyApprovalService,
    isAcknowledgeConflictsRequired,
    saveOption,
    bypassRequiredApprovals,
    showOptionalReviewers,
    canBypassRequiredApprovals,
  } = params;
  const alternateScheduledChangeForm = enableAlternateScheduledChangeForm();
  const reviewerFieldConfig = enableAlternateReviewerField();
  const showReviewerInFooter = reviewerFieldConfig === 'footer';
  const showReviewerInline = reviewerFieldConfig === 'inline';

  const isApprovalRequestedOrRequired = bypassRequiredApprovals ? false : isApprovalRequired || reviewerCount > 0;

  // Determine if we should show the inline reviewer field
  let showSecondaryReviewersPopover = false;
  let showReviewers = (() => {
    if (!usesLaunchDarklyApprovalService) {
      return false;
    }

    if (showReviewerInFooter) {
      showSecondaryReviewersPopover = !isUpdatingScheduledChange && !isApprovalRequired;
      if (bypassRequiredApprovals) {
        return showOptionalReviewers;
      }
      return showOptionalReviewers || (!isUpdatingScheduledChange && isApprovalRequired);
    }

    if (showReviewerInline) {
      return !isUpdatingScheduledChange && (!hideApprovalOption || isApprovalRequired);
    }

    return !isUpdatingScheduledChange && (!hideApprovalOption || isApprovalRequired);
  })();

  let showApprovalIntegration = !isUpdatingScheduledChange;
  let showCommentField = saveOption !== SaveOptions.DELETE || reviewerCount > 0;
  let showConfirmation = isConfirmationRequired && !isApprovalRequestedOrRequired;
  let showAcknowledgeConflicts = isAcknowledgeConflictsRequired;
  let showSecondaryScheduleChangesAction =
    alternateScheduledChangeForm && !isUpdatingScheduledChange && !hideScheduleOption;
  let showScheduledChangesForm = false;
  const showBypassRequiredApprovals = canBypassRequiredApprovals && !bypassRequiredApprovals && !showReviewerInline;

  if (alternateScheduledChangeForm) {
    showScheduledChangesForm = isUpdatingScheduledChange;
  } else {
    showScheduledChangesForm = (!hideScheduleOption && isCustomScheduleSelected) || isUpdatingScheduledChange;
  }
  // If test run is enabled, don't show confirmation or acknowledge conflicts
  if (testRunEnabled) {
    showConfirmation = false;
    showAcknowledgeConflicts = false;
    showReviewers = false;
    showSecondaryReviewersPopover = false;
    showCommentField = false;
    showScheduledChangesForm = false;
    showApprovalIntegration = false;
    showSecondaryScheduleChangesAction = false;
  }

  // If showing the old UI and custom schedule is selected, don't show confirmation or acknowledge conflicts -- this if statement will be cleaned up
  if (isCustomScheduleSelected && !isUpdatingScheduledChange && !alternateScheduledChangeForm) {
    showConfirmation = false;
    showAcknowledgeConflicts = false;
    showSecondaryReviewersPopover = false;
    showReviewers = false;
    showCommentField = false;
    showApprovalIntegration = false;
    showSecondaryScheduleChangesAction = false;
  }

  return {
    showContextEvaluations: testRunEnabled,
    showScheduledChangesForm,
    showApprovalIntegration,
    showReviewers,
    showComment: showCommentField,
    showConfirmation,
    showAcknowledgeConflicts,
    showSecondaryScheduleChangesAction,
    showSecondaryReviewersPopover,
    showReviewerInline,
    showReviewerInFooter,
    showBypassRequiredApprovals,
  };
};

export const getSaveButtonText = (params: {
  isCustomScheduleSelected: boolean;
  isUpdatingScheduledChange: boolean;
  isApprovalRequestedOrRequired: boolean;
  saveOption: SaveOptions;
  bypassRequiredApprovals: boolean;
}) => {
  const {
    isUpdatingScheduledChange,
    isCustomScheduleSelected,
    isApprovalRequestedOrRequired,
    saveOption,
    bypassRequiredApprovals,
  } = params;
  const isBypassingRequiredApprovals =
    saveOption === SaveOptions.SAVE_CHANGES_BYPASSING_APPROVAL || bypassRequiredApprovals;

  if (isUpdatingScheduledChange) {
    return 'Update scheduled change';
  }
  if (saveOption === SaveOptions.DELETE && !isApprovalRequestedOrRequired) {
    return 'Delete';
  }
  if (isCustomScheduleSelected) {
    return isBypassingRequiredApprovals ? 'Schedule without approval' : 'Schedule changes';
  }
  if (isBypassingRequiredApprovals) {
    return 'Save without approval';
  }
  if (isApprovalRequestedOrRequired) {
    return 'Request approval';
  }
  return 'Save changes';
};

export const getCancelButtonText = (params: {
  isJsonDiffingEnabled: boolean;
  testRunEnabled: boolean;
  isCustomScheduleSelected: boolean;
  useBackButton?: boolean;
}) => {
  const { isJsonDiffingEnabled, testRunEnabled, isCustomScheduleSelected, useBackButton } = params;

  if (isJsonDiffingEnabled || testRunEnabled || useBackButton) {
    return 'Back';
  }
  if (isCustomScheduleSelected) {
    return 'Back';
  }
  return 'Cancel';
};
